import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Compass, ArrowLeft, Mail, Home } from "lucide-react";
import { motion } from "framer-motion";

export default function NotFound() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const homeHref = user
    ? user.role === "admin"
      ? "/admin"
      : user.role === "logistics_team"
      ? "/team/logistics"
      : user.role === "accommodation_team"
      ? "/team/accommodation"
      : user.role === "security_team"
      ? "/team/security"
      : "/dashboard"
    : "/";

  return (
    <div className="min-h-screen bg-[#050914] flex flex-col items-center justify-center px-4">
      <div className="absolute inset-0 pool-tiles opacity-10 pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-md"
      >
        {/* Glow behind card */}
        <div className="absolute inset-0 rounded-2xl bg-[#00F0FF]/5 blur-2xl pointer-events-none" />

        <div className="relative bg-[#0A1128]/90 backdrop-blur-xl border border-[#00F0FF]/20 rounded-2xl p-8 md:p-10 text-center shadow-[0_0_60px_rgba(0,240,255,0.08)]">
          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mx-auto mb-6">
            <Compass className="w-8 h-8 text-[#00F0FF]" />
          </div>

          {/* Error code */}
          <div className="font-mono text-xs tracking-[0.4em] text-[#00F0FF]/60 uppercase mb-2">
            Error 404
          </div>

          <h1 className="font-display font-black text-3xl text-white mb-3">
            Off the Blocks
          </h1>

          <p className="text-[#64748B] text-sm leading-relaxed mb-6">
            The page you're looking for doesn't exist — it may have been moved, renamed, or the link might be off by a stroke.
          </p>

          {/* Contact block */}
          <div className="bg-[#050914]/60 border border-[#1E293B] rounded-xl p-4 mb-7 text-left">
            <p className="text-xs text-[#475569] mb-2 uppercase tracking-wider">Think this is a mistake?</p>
            <a
              href="mailto:support@asianmastersbangalore2026.com"
              className="flex items-center gap-2 text-sm text-[#00F0FF] hover:underline"
            >
              <Mail className="w-4 h-4 shrink-0" />
              support@asianmastersbangalore2026.com
            </a>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-[#1E293B] text-[#94A3B8] hover:text-white hover:border-[#2A3A5F] text-sm font-semibold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Go Back
            </button>
            <Link
              to={homeHref}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/15 text-sm font-semibold transition-colors"
            >
              <Home className="w-4 h-4" /> {user ? "Dashboard" : "Home"}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-[#334155] mt-6">
          AMSC India 2026 · Bengaluru
        </p>
      </motion.div>
    </div>
  );
}
